import { useModal } from '@/hooks/useModal';
import AddPipelineModalV2 from '@/ui/components/addPipelineModal/AddPipelineModalV2';
import useDraftUpdatePipeline from '@/ui/pages/pipelineConfigPage/api/mutations/useDraftUpdatePipeline';
import DeletePipelineModal from '@/ui/pages/pipelineConfigPage/components/modals/DeletePipelineModal';
import DiscardChangesModal from '@/ui/pages/pipelineConfigPage/components/modals/DiscardChangesModal';
import RenamePipelineModal from '@/ui/pages/pipelineConfigPage/components/modals/RenamePipelineModal';
import SaveChangesModal from '@/ui/pages/pipelineConfigPage/components/modals/SaveChangesModal';
import { ColourPickerWithCustomPicker } from '@/ui/pages/pipelineConfigPage/components/stageConfig/ColourPicker';
import { useDraftMode } from '@/ui/pages/pipelineConfigPage/context/DraftModeContext';
import { usePageValidation } from '@/ui/pages/pipelineConfigPage/context/PageValidationContext';
import { Alert, Button, ButtonDropdown, Tooltip, H3 } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { useHistory } from 'react-router';

type Props = {
  pipelineId: string;
  title?: string;
  colour?: string;
  isDeletable?: { result: boolean; reasons: string[] };
};

const PipelineConfigHeader: FC<Props> = ({
  pipelineId,
  title,
  colour,
  isDeletable,
}) => {
  const history = useHistory();

  /**
   * state
   */
  const { phase, pendingCount } = useDraftMode();
  const { hasValidationErrors } = usePageValidation();
  const [modalState, modalDispatch] = useModal<
    'RENAME' | 'DUPLICATE' | 'DELETE' | 'CONFIRM_SAVE' | 'CONFIRM_DISCARD',
    {
      RENAME: undefined;
      DUPLICATE: undefined;
      DELETE: undefined;
      CONFIRM_SAVE: undefined;
      CONFIRM_DISCARD: undefined;
    }
  >({ activeModal: null });

  /**
   * mutations
   */
  const { mutate } = useDraftUpdatePipeline({ pipelineId });

  const hasUnsavedChanges =
    pendingCount > 0 ||
    phase.startsWith('committing') ||
    phase.startsWith('cancelling');

  return (
    <>
      <div className="flex justify-between p-4 pb-0 flex-wrap gap-4">
        <ButtonDropdown
          variant="quaternary"
          size="xs"
          className="-mx-2"
          dropdownOptions={{
            items: [
              {
                icon: 'edit-01.3',
                label: 'Rename pipeline',
                onClick: () => modalDispatch({ type: 'RENAME' }),
              },
              {
                icon: 'copy-07.3',
                label: 'Duplicate pipeline',
                onClick: () => modalDispatch({ type: 'DUPLICATE' }),
              },
              {
                label: (
                  <div className="text-red-500 flex items-center gap-2.5">
                    <UntitledIcon name="trash-01.3" className="size-5" />
                    Delete pipeline
                  </div>
                ),
                onClick: () => modalDispatch({ type: 'DELETE' }),
                disabled: isDeletable?.result === false,
                disabledReasons: isDeletable?.reasons,
              },
            ],
            anchor: 'start',
            footer: (
              <div className="p-3">
                <ColourPickerWithCustomPicker
                  value={colour}
                  onChange={(hex) => mutate({ id: pipelineId, colour: hex })}
                />
              </div>
            ),
          }}
        >
          <div className="flex items-center gap-2">
            <UntitledIcon
              name={'circle.4'}
              className="w-5"
              style={{ color: colour }}
            />
            <H3>Manage '{title}'</H3>
          </div>
          <UntitledIcon name="dots-vertical.3" className="size-5" />
        </ButtonDropdown>

        {hasUnsavedChanges && (
          <div className="flex gap-2.5">
            <Alert key="unsaved-changes" colour="yellow" className="py-2">
              You have unsaved changes. Please save or discard before leaving.
            </Alert>

            <Button
              size="sm"
              variant="secondary"
              onClick={() => modalDispatch({ type: 'CONFIRM_DISCARD' })}
              isProcessing={phase.startsWith('cancelling')}
            >
              Discard changes
            </Button>
            <Tooltip
              enabled={hasValidationErrors}
              tooltipContent="You have validation errors"
            >
              <Button
                size="sm"
                onClick={() => modalDispatch({ type: 'CONFIRM_SAVE' })}
                isProcessing={phase.startsWith('committing')}
                disabled={hasValidationErrors}
              >
                Save changes
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
      <RenamePipelineModal
        isOpen={modalState.activeModal === 'RENAME'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
        pipelineId={pipelineId}
        currentTitle={title}
      />
      <AddPipelineModalV2
        isOpen={modalState.activeModal === 'DUPLICATE'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
        initialTemplatePipelineId={pipelineId}
        onSuccess={(newPipelinePublicId) => {
          modalDispatch({ type: 'CLOSE_MODAL' });
          history.push(`/pipelines/${newPipelinePublicId}/config`);
        }}
      />
      <DeletePipelineModal
        isOpen={modalState.activeModal === 'DELETE'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
        pipelineId={pipelineId}
      />
      <SaveChangesModal
        isOpen={modalState.activeModal === 'CONFIRM_SAVE'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
      />
      <DiscardChangesModal
        isOpen={modalState.activeModal === 'CONFIRM_DISCARD'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
      />
    </>
  );
};

export default PipelineConfigHeader;
